import React, { useState, useEffect } from 'react';
import { deleteUser, reauthenticateWithCredential, EmailAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '../../Firebase';
import { useNavigate, Navigate } from 'react-router-dom';

interface DeletePageProps {}

const DeletePage: React.FC<DeletePageProps> = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [password, setPassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(FIREBASE_AUTH.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(FIREBASE_AUTH, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleInitialDelete = () => {
    setShowConfirmation(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!currentUser || !password.trim()) {
      setError('Please enter your password to confirm deletion.');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      // Re-authenticate the user before deletion (required for sensitive operations)
      const credential = EmailAuthProvider.credential(currentUser.email!, password);
      await reauthenticateWithCredential(currentUser, credential);

      // Delete user document from Firestore (if you store user data there)
      try {
        await deleteDoc(doc(FIREBASE_FIRESTORE, 'users', currentUser.uid));
      } catch (firestoreError) {
        console.warn('No user document found in Firestore or error deleting:', firestoreError);
        // Continue with account deletion even if Firestore document doesn't exist
      }

      // Delete the Firebase Auth account
      await deleteUser(currentUser);

      // Redirect to login or home page
      navigate('/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      
      if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please log out and log back in, then try deleting your account again.');
      } else {
        setError('Failed to delete account. Please try again or contact support.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (showConfirmation) {
      // If in confirmation step, go back to initial step
      setShowConfirmation(false);
      setPassword('');
      setError('');
    } else {
      // If on initial step, sign out and redirect to login
      handleSignOut();
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(FIREBASE_AUTH);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // If sign out fails, still redirect to login
      navigate('/login');
    }
  };

  // Show loading while determining auth state
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem auto'
          }}></div>
          <p>Loading...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login?redirect=/deleteaccount" replace />;
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        width: '100%',
        padding: '2rem',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
        backgroundColor: '#f9f9f9'
      }}>
      {/* Return to Unison button in top left of the white box */}
      <button
        onClick={() => window.location.href = 'https://unison.org.uk'}
        style={{
          position: 'absolute',
          top: '0.5rem',
          left: '0.5rem',
          padding: '0.4rem 0.8rem',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '0.8rem',
          zIndex: 10,
          marginBottom: '1rem'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#545b62';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#6c757d';
        }}
      >
        Return to UNISON
      </button>

      <h2 style={{ color: '#d32f2f', marginBottom: '1rem', marginTop: '2.5rem', fontWeight: 'bold' }}>Delete Account</h2>
      
      {!showConfirmation ? (
        <div>
          <div style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Warning</h3>
            <p style={{ margin: 0, color: '#856404' }}>
              This action cannot be undone. Deleting your account will permanently remove:
            </p>
            <ul style={{ color: '#856404', marginTop: '0.5rem' }}>
              <li>Your account and profile information</li>
              <li>All associated data and settings</li>
              {/* <li>Access to all services</li> */}
            </ul>
          </div>

          <p style={{ marginBottom: '1.5rem', color: '#666' }}>
            Current account: <strong>{currentUser.email}</strong>
          </p>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                cursor: 'pointer',
                flex: 1
              }}
            >
              Sign Out
            </button>
            <button
              onClick={handleInitialDelete}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: '#d32f2f',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer',
                flex: 1
              }}
            >
              Delete Account
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#c62828' }}>Final Confirmation</h3>
            <p style={{ margin: 0, color: '#c62828' }}>
              Please enter your password to permanently delete your account.
            </p>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#333'
            }}>
              Password:
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
              disabled={isDeleting}
            />
          </div>

          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #ffcdd2'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleCancel}
              disabled={isDeleting}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                flex: 1,
                opacity: isDeleting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting || !password.trim()}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                backgroundColor: isDeleting || !password.trim() ? '#ccc' : '#d32f2f',
                color: 'white',
                borderRadius: '4px',
                cursor: isDeleting || !password.trim() ? 'not-allowed' : 'pointer',
                flex: 1
              }}
            >
              {isDeleting ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default DeletePage;